import {Ticket} from '@naver/massronome-v2-sdk'
import {createAction} from '@reduxjs/toolkit'

import {qs} from '@shared/helpers/queryString'

import {
    OrderSheetPageSet,
    OrderSheetPageSetType,
    UserAuthStatusType,
    WaitingExpelledReasonType,
    WaitingStatusType,
} from '@web/core/constants'
import getApi, {createApiAsyncThunk, getBaseUrl} from '@web/helpers/api'
import {LanguageType} from '@web/i18n'
import {commonSelector, pageSetInfoSelector} from '@web/slices/common/slice'
import {ticketIdSelector, WaitQueue} from '@web/slices/waiting/slice'

import {
    ThunkApi,
    WaitingInfo,
    WaitingStateResponse,
    WaitingInfoResponse,
    WaitQueueRefreshEvent,
    TicketExpelEvent,
} from './interfaces'

/** 대기열 상태 조회 API  */
const WAITING_STATE_URL: Record<OrderSheetPageSetType, string> = {
    [OrderSheetPageSet.EASYBOOKING]: '/orderSheet/easyBooking/waiting/state',
    [OrderSheetPageSet.MALL]: '/orderSheet/mall/waiting/state',
    [OrderSheetPageSet.SELLER]: '/orderSheet/seller/waiting/state',
    // 패밀리 결제는 대기열이 없습니다.
    [OrderSheetPageSet.FAMILY_PAY]: '',
    // 클레임 비용결제는 대기열이 없습니다.
    [OrderSheetPageSet.CLAIM]: '',
}

const WAITING_INFO_URL: Record<OrderSheetPageSetType, string> = {
    [OrderSheetPageSet.EASYBOOKING]: '/orderSheet/easyBooking/waiting/info',
    [OrderSheetPageSet.MALL]: '/orderSheet/mall/waiting/info',
    [OrderSheetPageSet.SELLER]: '/orderSheet/seller/waiting/info',
    // 패밀리 결제는 대기열이 없습니다.
    [OrderSheetPageSet.FAMILY_PAY]: '',
    // 클레임 비용결제는 대기열이 없습니다.
    [OrderSheetPageSet.CLAIM]: '',
}

/** 대기열 상태 조회 액션 */
export const fetchWaitingState = createApiAsyncThunk<
    {
        enterable: boolean
        ticketId: Maybe<string>
        ticket: Maybe<Ticket>
        version?: Maybe<'V1' | 'V2'>
        languageCode?: LanguageType
        userAuthStatus?: UserAuthStatusType
    },
    void,
    ThunkApi<unknown>
>('checkWaitingState', async (_, {getState}) => {
    const state = getState()
    const {seqID} = commonSelector(state)
    const {orderSheetPageSet} = pageSetInfoSelector(state)
    if (!orderSheetPageSet) {
        throw new Error('pageSet을 확인할 수 없습니다.')
    }
    const baseUrl = getBaseUrl(orderSheetPageSet)
    const api = getApi(baseUrl)
    const {
        data: {result},
    } = await api.get<{result: WaitingStateResponse}>(
        qs.stringifyUrl({
            url: WAITING_STATE_URL[orderSheetPageSet],
            query: {
                orderSheetId: seqID,
            },
        }),
    )
    // error catch 를 위해, 값을 꺼내어 확인합니다.
    return result
})

/** 대기열에서 주문서로의 입장 요청 액션 */
export const requestEnter = createApiAsyncThunk<
    {
        enterable: boolean
        ticketId: Maybe<string>
        ticket: Maybe<Ticket>
        version?: Maybe<'V1' | 'V2'>
        languageCode?: LanguageType
        userAuthStatus?: UserAuthStatusType
    },
    void,
    ThunkApi<unknown>
>('requestEnter', async (_, {getState}) => {
    const state = getState()
    const {seqID} = commonSelector(state)
    const {orderSheetPageSet} = pageSetInfoSelector(state)
    if (!orderSheetPageSet) {
        throw new Error('pageSet을 확인할 수 없습니다.')
    }
    const baseUrl = getBaseUrl(orderSheetPageSet)
    const api = getApi(baseUrl)
    const {
        data: {result},
    } = await api.get<{result: WaitingStateResponse}>(
        qs.stringifyUrl({
            url: WAITING_STATE_URL[orderSheetPageSet],
            query: {
                orderSheetId: seqID,
            },
        }),
    )
    // error catch 를 위해, 값을 꺼내어 확인합니다.
    return result
})

/** 대기열 정보 조회 액션 */
export const fetchWaitingInfo = createApiAsyncThunk<
    WaitingInfo,
    undefined | {requireSocketAuthUrl?: boolean},
    ThunkApi
>('checkWaitingInfo', async (arg, {getState}) => {
    const state = getState()
    const ticketId = ticketIdSelector(state)
    const {orderSheetPageSet} = pageSetInfoSelector(state)
    if (!orderSheetPageSet) {
        throw new Error('pageSet을 확인할 수 없습니다.')
    }
    const baseUrl = getBaseUrl(orderSheetPageSet)
    const api = getApi(baseUrl)
    const {
        data: {result},
    } = await api.get<{result: WaitingInfoResponse}>(
        qs.stringifyUrl({
            url: WAITING_INFO_URL[orderSheetPageSet],
            query: {
                ticketId,
                requireSocketAuthUrl: arg?.requireSocketAuthUrl ?? true,
            },
        }),
    )
    return result
})

/** 대기열 정보 재조회 액션 */
export const setWaitingInfo = createApiAsyncThunk<WaitingInfo, undefined | {requireSocketAuthUrl?: boolean}, ThunkApi>(
    'setWaitingInfo',
    async (arg, {getState}) => {
        const state = getState()
        const ticketId = ticketIdSelector(state)
        const {orderSheetPageSet} = pageSetInfoSelector(state)
        if (!orderSheetPageSet) {
            throw new Error('pageSet을 확인할 수 없습니다.')
        }
        const baseUrl = getBaseUrl(orderSheetPageSet)
        const api = getApi(baseUrl)
        const {
            data: {result},
        } = await api.get<{result: WaitingInfoResponse}>(
            qs.stringifyUrl({
                url: WAITING_INFO_URL[orderSheetPageSet],
                query: {
                    ticketId,
                },
            }),
        )
        return result
    },
)

/** 대기열 상태 업데이트 */
export const setWaitingStatus = createAction<WaitingStatusType>('waiting/setWaitingStatus')

/** 전역 대기열의 대기 정보 업데이트 */
export const setGlobalWaitQueue = createAction<WaitQueue>('waiting/setGlobalWaitQueue')

/** 부분 대기열의 대기 정보 업데이트 */
export const setSubWaitQueue = createAction<WaitQueue & {queueId: string}>('waiting/setSubWaitQueue')

/** 대기열 추방 */
export const expel = createAction<{
    /** 대기표 ID */
    ticketId: string
    /** 추방 사유 */
    reason: Maybe<WaitingExpelledReasonType>
}>('waiting/expel')

/** 대기열 큐 정보 업데이트 (V2) */
export const setWaitingQueueInfo = createAction<WaitQueueRefreshEvent>('waiting/setWaitingInfo')

/** 대기열 추방 (V2) */
export const setExpel = createAction<TicketExpelEvent>('waiting/setExpel')
